import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction, Slice, SliceCaseReducers } from '@reduxjs/toolkit'

import { Notification } from '../../values/Notification'
import type { NewNotificationInput } from '../../values/Notification'
import { ActionTypes } from '../ActionTypes'
import type { RootStateWithComposer } from '../getWiredComposerStore'

export type NotificationsState = {
  visibleNotifications: Notification[]
}

type NotificationSelectorState =
  | {
      visibleNotifications: Notification[]
      composer?: never
    }
  | {
      composer: RootStateWithComposer['composer']
    }

type RemoveAllNotificationsByScopePrefixPayload = {
  scopePrefix: string
}

type QueueNotificationAction = PayloadAction<NewNotificationInput>

const initialState: NotificationsState = {
  visibleNotifications: [],
}

export const notificationsSlice: Slice<
  NotificationsState,
  SliceCaseReducers<NotificationsState>,
  'notifications'
> = createSlice({
  // available at state.composer.notificaitons
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.visibleNotifications.push(action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action): action is QueueNotificationAction =>
        action.type === ActionTypes.QUEUE_NOTIFICATION,
      (state, action) => {
        state.visibleNotifications.push(Notification.new(action.payload))
      },
    )
    builder.addMatcher(
      (
        action,
      ): action is PayloadAction<RemoveAllNotificationsByScopePrefixPayload> =>
        action.type === ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE_PREFIX,
      (state, action) => {
        state.visibleNotifications = state.visibleNotifications.filter(
          (notification) =>
            !notification.scope?.startsWith(action.payload.scopePrefix),
        )
      },
    )
  },
})

export const selectVisibleNotifications = (
  state: NotificationSelectorState,
): Notification[] => {
  if (!state.composer) {
    // old state, remove when NotificationStore is gone
    return state.visibleNotifications
  }

  return state.composer[notificationsSlice.name].visibleNotifications
}

export const { addNotification } = notificationsSlice.actions
